import { useState } from "react";
import { Box, Typography, Rating, TextField, Button, Stack } from "@mui/material";
import { Star } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { RATING_DIMENSIONS } from "@/constants/ratings";
import type { Ratings } from "@/types";

interface ReviewFormProps {
  initialRatings?: Ratings;
  initialComment?: string;
  submitLabel: string;
  onSubmit: (ratings: Ratings, comment?: string) => void;
  loading?: boolean;
}

const DEFAULT_RATING = 3;

export default function ReviewForm({
  initialRatings,
  initialComment,
  submitLabel,
  onSubmit,
  loading = false,
}: ReviewFormProps) {
  const { t } = useTranslation();
  const [ratings, setRatings] = useState<Ratings>(
    initialRatings ?? {
      services: DEFAULT_RATING,
      support: DEFAULT_RATING,
      website: DEFAULT_RATING,
      pricing: DEFAULT_RATING,
      performance: DEFAULT_RATING,
    },
  );
  const [comment, setComment] = useState(initialComment ?? "");

  const handleSubmit = () => {
    onSubmit(ratings, comment || undefined);
  };

  return (
    <Box>
      <Stack spacing={2}>
        {RATING_DIMENSIONS.map(({ key, labelKey }) => (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              {t(labelKey)}
            </Typography>
            <Rating
              value={ratings[key]}
              onChange={(_, value) =>
                setRatings((prev) => ({ ...prev, [key]: value ?? 1 }))
              }
              icon={<Star fontSize="inherit" />}
              emptyIcon={<Star fontSize="inherit" />}
              sx={{ color: "primary.main" }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
              }}
            >
              {ratings[key]}
            </Typography>
          </Box>
        ))}
      </Stack>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t("review.commentPlaceholder")}
        slotProps={{ htmlInput: { maxLength: 2000 } }}
        sx={{ mt: 3 }}
      />
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        sx={{ mt: 2 }}
        fullWidth
      >
        {submitLabel}
      </Button>
    </Box>
  );
}
